<template>
  <span
    v-if="resolved.severity !== COVERAGE_GAP_SEVERITY.NONE"
    class="coverage-gap-badge"
    :class="`coverage-gap-badge--${resolved.severity}`"
  >
    <span class="coverage-gap-badge__dot" aria-hidden="true"></span>
    <span class="coverage-gap-badge__label">{{ resolved.label }}</span>
  </span>
</template>

<script setup>
import { computed } from "vue";
import dayjs from "dayjs";
import { resolveCoverageGap, COVERAGE_GAP_SEVERITY } from "./coverageGapBadgeUtils.js";

/**
 * Coverage-gap pill for the Fair Pay > Positions list (ER-11714).
 *
 * Rendered as a second pill beside the pay range subscription card, only when
 * the subscription has a coverage gap in force (danger: spans today) or in the
 * future (warning). Follows the exIQtive status-pill pattern documented on
 * InspectionRequestStatusBadge: leading colour dot, Inter 500 12/18, fully
 * rounded, with the per-ramp colours from the ticket's Badge spec.
 */
const props = defineProps({
  /** Resolved coverage gaps from mapSubscription(): [{ from, to }]. */
  coverageGaps: {
    type: Array,
    default: () => [],
  },
  /** Reference "today" (the list's as-of date). Defaults to the actual today. */
  today: {
    type: String,
    default: () => dayjs().format("YYYY-MM-DD"),
  },
});

const resolved = computed(() => resolveCoverageGap(props.coverageGaps, props.today));
</script>

<style scoped>
/* Base pill — same geometry as InspectionRequestStatusBadge (gap 4, pad 2/8/2/6,
   radius-full, Inter 500 12/18). white-space normal so the pill wraps rather than
   truncates when the Status column is narrow. */
.coverage-gap-badge {
  display: inline-flex;
  align-items: center;
  text-align: center;
  font-family: Inter;
  gap: 4px;
  padding: 2px 8px 2px 6px;
  border-radius: 9999px;
  border: 1px solid transparent;
  font-weight: 500;
  font-size: 12px;
  line-height: 18px;
  white-space: normal;
  overflow-wrap: break-word;
}

.coverage-gap-badge__dot {
  width: 8px;
  height: 8px;
  border-radius: 9999px;
  flex-shrink: 0;
}

/* Danger — a gap spans today. bg #FCEBEB, text #A32D2D, dot #E24B4A. */
.coverage-gap-badge--danger {
  background-color: #fcebeb;
  color: #a32d2d;
}
.coverage-gap-badge--danger .coverage-gap-badge__dot {
  background-color: #e24b4a;
}

/* Warning — all gaps start after today. bg #FAEEDA, text #854F0B, dot #BA7517. */
.coverage-gap-badge--warning {
  background-color: #faeeda;
  color: #854f0b;
}
.coverage-gap-badge--warning .coverage-gap-badge__dot {
  background-color: #ba7517;
}
</style>
